import { ref, onMounted, onUnmounted } from 'vue'
import {
  getThemePreference,
  setThemePreference,
  applyThemeToDOM,
} from '@/utils/preferences'
import { storageKey } from '@/services/types'
import { useAccountSettingsStore } from '@/stores/account-settings'
import type { ThemePreference } from '@/types/user-settings'

const currentTheme = ref<ThemePreference>(getThemePreference())

let mediaQuery: MediaQueryList | null = null

// 修复项：使用 BroadcastChannel 实现跨标签页主题同步。
// 优先使用 BroadcastChannel（不触发当前标签页自身事件，更可靠），
// 对不支持 BroadcastChannel 的浏览器降级使用 window.storage 事件。
const themeChannel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('ledger_ai_theme')
  : null

export function useTheme() {
  function apply() {
    applyThemeToDOM(currentTheme.value)
  }

  function setTheme(theme: ThemePreference) {
    currentTheme.value = theme
    setThemePreference(theme)
    apply()
    // 修复项：通知其他标签页主题已变更，自身标签页不会收到该消息。
    themeChannel?.postMessage({ theme })

    // 修复项：异步同步到后端用户资料，实现多设备偏好一致性。
    // 使用 setTimeout 避免阻塞 UI；后端同步失败不应影响本地体验。
    setTimeout(() => {
      const store = useAccountSettingsStore()
      store.savePreferences({ theme })
    }, 0)
  }

  // 修复项：将未使用的参数 _e 改为 _event，并保留参数以兼容媒体查询事件回调和手动调用两种场景。
  function handleSystemChange(_event: MediaQueryListEvent | MediaQueryList) {
    if (currentTheme.value === 'system') {
      apply()
    }
  }

  // 修复项：监听其他标签页通过 BroadcastChannel 发送的主题变更，实现跨标签页同步。
  function handleBroadcastMessage(event: MessageEvent) {
    if (event.data?.theme) {
      const newTheme = event.data.theme as ThemePreference
      currentTheme.value = newTheme
      setThemePreference(newTheme)
      apply()
    }
  }

  // 修复项：降级方案 - 监听 storage 事件，用于不支持 BroadcastChannel 的浏览器。
  function handleStorageChange(event: StorageEvent) {
    if (event.key === storageKey('theme') && event.newValue) {
      const newTheme = event.newValue as ThemePreference
      currentTheme.value = newTheme
      apply()
    }
  }

  onMounted(() => {
    // 修复项：移除 onMounted 中的重复 apply()。
    // main.ts 已在应用 mount 前调用 initTheme() 完成 DOM 初始化；
    // useTheme 作为模块级 composable，onMounted 会在每个使用它的组件挂载时触发，
    // 保留 apply 会导致从其他页面切回时执行无意义的 DOM 操作。
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', handleSystemChange)
    themeChannel?.addEventListener('message', handleBroadcastMessage)
    window.addEventListener('storage', handleStorageChange)
  })

  onUnmounted(() => {
    if (mediaQuery) {
      mediaQuery.removeEventListener('change', handleSystemChange)
    }
    themeChannel?.removeEventListener('message', handleBroadcastMessage)
    window.removeEventListener('storage', handleStorageChange)
  })

  return {
    currentTheme,
    setTheme,
    apply,
  }
}

// For non-setup usage (e.g. main.ts)
export function initTheme() {
  const theme = getThemePreference()
  applyThemeToDOM(theme)
  currentTheme.value = theme
}
